import { CalculatedFees } from '@/apis/payments/requests'
import {
  PaymentDetailsResponse,
  POSTPaymentDetails,
} from '@/apis/providers/requests'
import { PaymentPayload } from '@/apis/providers/types/provider.types'
import { useAppAlert } from '@/app/AppAlertProvider'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card'
import Image from '@/app/common/components/Image/Image'
import { displayTotalAmountToBePaidByApplicant } from '@/app/common/payment.utils'
import { cn, currencyFormatter } from '@/app/common/utilities'
import { getTotalServiceFeeToBePaidByApplicant } from '@/app/common/utils/payment/utils'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import { ReviewItem } from '@/app/pages/family_application/review/ReviewItem'
import { SubmitButton } from '@/app/pages/family_application/student_details/shared/submit-button.tsx'
import { PaymentCategories, PursesArray } from '@/schemas/provider.schema'
import {
  resetPayProviderData,
  setPayProviderSubmitted,
} from '@/storage/applications/providerPaySlice'
import {
  MutationStatus,
  useMutation,
  UseMutationResult,
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { ProviderPaymentDetails } from '../../../create_provider_request_form'
import ProviderPaySuccess from '../../../ProviderPaySuccess'
import { RequestError } from '@/common/types_and_interfaces.ts'

interface Props {
  expenses: PaymentCategories
  paymentDetails: ProviderPaymentDetails
  onSubmit: () => void
  mutationStatus: MutationStatus
  apiPurses: PursesArray
  paymentFees: CalculatedFees
}

export default function ReviewSummary({
  expenses,
  paymentDetails,
  mutationStatus,
  onSubmit,
  apiPurses,
  paymentFees,
}: Props) {
  const { fireAlert } = useAppAlert()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const mutation: UseMutationResult<
    PaymentDetailsResponse,
    RequestError,
    PaymentPayload
  > = useMutation({
    mutationFn: (payload: PaymentPayload) => POSTPaymentDetails(payload),
    onSuccess: () => {
      dispatch(setPayProviderSubmitted(true))
      dispatch(resetPayProviderData())
    },
    onError: (error) => {
      fireAlert({
        variant: 'error',
        message: error.message,
      })
    },
  })

  const providerDetail = [
    {
      title: t('Name'),
      value:
        paymentDetails.provider.name + ', ' + paymentDetails.provider.location,
    },
  ]

  const orderDetail = [
    {
      title: t('payment_amount'),
      value: displayTotalAmountToBePaidByApplicant(
        {
          base_amount: paymentFees.base_amount,
          service_fee: paymentFees.service_fee,
          net_amount: paymentFees.net_amount,
          serviceFeePayer: paymentFees.serviceFeePayer,
        },
        'Pay Provider'
      ),
    },
    {
      title: t('service_fee'),
      value: getTotalServiceFeeToBePaidByApplicant(
        paymentFees.service_fee,
        paymentFees.serviceFeePayer
      ),
    },
    {
      title: t('amount_to_provider'),
      value: paymentFees.base_amount,
    },
  ]

  const expenseDetail = [
    {
      title: t('expense_category'),
      value: paymentDetails.expenseCategories
        ?.map(
          (category) => expenses?.find((item) => item.id === category.id)?.name
        )
        .join(', '),
    },
    {
      title: t('comment'),
      value: paymentDetails.comments,
    },
    {
      title: t('Invoice #'),
      value: paymentDetails.invoiceNumber,
    },
  ]

  if (mutation.status === 'success') {
    return <ProviderPaySuccess status={mutation.data.status} />
  }

  const isAppeal = window.location.pathname.includes('appeal')

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{t('order_details')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className={cn('w-full space-y-2')}>
        <Card className="">
          <CardHeader className="flex items-center justify-between">
            <h2 className="font-bold text-gray-700">{t('provider')}</h2>
          </CardHeader>

          <CardContent>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {providerDetail.map((detail, index) => (
                <ReviewItem
                  key={index}
                  title={detail.title}
                  value={detail.value}
                />
              ))}
            </ul>
          </CardContent>
        </Card>
        <Card className="mb-8">
          <CardHeader className="flex items-center justify-between">
            <h2 className="font-bold text-gray-700">{t('order_details')}</h2>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between"></div>
            <div>
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {orderDetail.map((detail, index) => (
                  <ReviewItem
                    key={index}
                    title={detail.title}
                    value={currencyFormatter.format(detail.value)}
                  />
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="font-bold text-gray-700">Documents</CardHeader>
          <CardContent className="grid grid-cols-2 lg:grid-cols-3 gap-4 ">
            {paymentDetails.documents.map((document) => {
              return (
                <div className="border border-gray-300 h-40 w-40 overflow-hidden rounded-lg">
                  <Image
                    allowZoom={true}
                    className="object-cover w-full h-full"
                    path={document}
                  />
                </div>
              )
            })}
          </CardContent>
        </Card>
        <Card className="mb-8">
          <CardHeader className="flex items-center justify-between">
            <h2 className="font-bold text-gray-700">{t('purse')}</h2>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {paymentDetails.purses.map((purse, index) => {
                const studentName = apiPurses?.find(
                  (item) => item.id === purse.purse_id
                )?.student_name
                return (
                  <li className="py-3 sm list-none" key={index}>
                    <div className="flex items-center space-x-4 rtl:space-x-reverse justify-between">
                      <div className="">
                        <p className="text-sm font-medium dark:text-white">
                          {t(purse.name)}
                        </p>
                        <p className="text-sm text-gray-600">{studentName}</p>
                      </div>
                      <p className="text-gray-800 text-sm">
                        {currencyFormatter.format(purse.amount)}{' '}
                      </p>
                    </div>
                  </li>
                )
              })}
            </ul>
          </CardContent>
        </Card>
        <Card className="mb-8">
          <CardHeader className="flex items-center justify-between">
            <h2 className="font-bold text-gray-700">{t('expense_type')}</h2>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {expenseDetail.map((detail, index) => (
                <ReviewItem
                  orientation="vertical"
                  key={index}
                  title={detail.title}
                  value={detail.value}
                />
              ))}
            </ul>
          </CardContent>
        </Card>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar>
        <SubmitButton
          buttonTxt={isAppeal ? t('submit_appeal') : t('submit_payment')}
          onClick={onSubmit}
          status={mutationStatus}
        />
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
